import {
	Button,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	Input,
	Label,
	ScrollArea,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components';
import { useMemo, useState, useEffect } from 'react';
import { PageLimit } from '@/components/TablePersonalizada';
import { useLogViewerContext } from '../context/LogViewerProvider';
import { useLogsByTable, useLogsByTableAndRecord } from '@/services/useLogService';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Loader2 } from 'lucide-react';
import { formatTimeAgo } from '@/helpers/formatTimeAgo';

const DEFAULT_PAGINATION: PageLimit = {
	page: 1,
	limit: 10,
	searchInAll: '',
};

export const LogViewerModal = () => {
	const { state, close } = useLogViewerContext();
	const [pagination, setPagination] = useState<PageLimit>(DEFAULT_PAGINATION);

	useEffect(() => {
		if (!state.isOpen) {
			setPagination(DEFAULT_PAGINATION);
			return;
		}
		setPagination({
			...DEFAULT_PAGINATION,
			...state.initialParams,
			page: 1,
		});
	}, [state.isOpen, state.initialParams, state.mode, state.table, state.idRegister]);

	const queryParams = useMemo(() => pagination, [pagination]);

	const logsByTableQuery = useLogsByTable(state.table, queryParams, {
		enabled: state.isOpen && state.mode === 'table' && Boolean(state.table),
	});

	const logsByRecordQuery = useLogsByTableAndRecord(
		state.table,
		state.idRegister ?? '',
		queryParams,
		{
			enabled:
				state.isOpen &&
				state.mode === 'record' &&
				Boolean(state.table) &&
				Boolean(state.idRegister),
		},
	);

	const activeQuery = state.mode === 'record' ? logsByRecordQuery : logsByTableQuery;
	const logs = activeQuery.data?.dataLog ?? [];
	const totalItems = activeQuery.data?.pagination.total ?? 0;
	const totalPages =
		totalItems > 0 ? Math.ceil(totalItems / (pagination.limit || DEFAULT_PAGINATION.limit)) : 1;

	const isLoading = activeQuery.isLoading || activeQuery.isFetching;

	const title =
		state.mode === 'record'
			? `Registro de cambios del ${state.label ?? state.table}`
			: `Logs de ${state.label ?? state.table}`;

	const description =
		state.mode === 'record'
			? `Mostrando los eventos asociados al registro ${state.idRegister}.`
			: `Histórico general de la tabla ${state.table}.`;

	const handleSearchChange = (value: string) => {
		setPagination(prev => ({
			...prev,
			page: 1,
			searchInAll: value,
		}));
	};

	const handleLimitChange = (value: string) => {
		const numeric = Number(value);
		if (Number.isNaN(numeric)) return;
		setPagination(prev => ({
			...prev,
			limit: numeric,
			page: 1,
		}));
	};

	const handlePageChange = (direction: 'prev' | 'next') => {
		setPagination(prev => {
			const nextPage =
				direction === 'prev'
					? Math.max(1, prev.page - 1)
					: Math.min(totalPages, prev.page + 1);
			return {
				...prev,
				page: nextPage,
			};
		});
	};

	return (
		<Dialog open={state.isOpen} onOpenChange={open => (!open ? close() : undefined)}>
			<DialogContent className="max-w-5xl w-[96vw] p-0 sm:p-0 overflow-hidden">
				<div className="flex h-full max-h-[90vh] flex-col gap-4 p-4 sm:p-6">
					<DialogHeader className="shrink-0">
						<DialogTitle>{title}</DialogTitle>
						<DialogDescription>{description}</DialogDescription>
					</DialogHeader>

					<div className="flex w-full flex-col gap-3 sm:flex-row">
						<div className="flex-1">
							<Label htmlFor="log-search">Buscar en mensajes</Label>
							<Input
								id="log-search"
								placeholder="Usuario, mensaje, ID..."
								value={pagination.searchInAll ?? ''}
								onChange={event => handleSearchChange(event.target.value)}
							/>
						</div>
						<div className="flex w-full sm:w-auto sm:max-w-[200px]">
							<div className="w-full">
								<Label>Registros por página</Label>
								<Select
									value={String(pagination.limit)}
									onValueChange={handleLimitChange}>
									<SelectTrigger>
										<SelectValue placeholder="Límites" />
									</SelectTrigger>
									<SelectContent>
										{[10, 20, 50, 100].map(item => (
											<SelectItem key={item} value={String(item)}>
												{item}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						</div>
					</div>

					<div className="flex-1 rounded-md border">
						<div className="h-full max-h-[60vh] min-h-[260px]">
							{isLoading ? (
								<div className="flex h-full items-center justify-center">
									<Loader2 className="h-8 w-8 animate-spin" />
								</div>
							) : logs.length === 0 ? (
								<div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center">
									<p className="text-lg font-semibold">Sin registros</p>
									<p className="text-sm text-muted-foreground">
										No encontramos eventos con los filtros actuales.
									</p>
								</div>
							) : (
								<>
									{/* Versión mobile en tarjetas */}
									<ScrollArea className="h-full w-full sm:hidden pr-1">
										<div className="flex flex-col gap-3">
											{logs.map(log => (
												<div
													key={log.id}
													className="rounded-lg border border-border bg-background/70 p-3 shadow-sm">
													<div className="flex flex-col gap-1">
														<div className="text-xs text-muted-foreground uppercase">
															Fecha
														</div>
														<div className="text-sm font-semibold">
															{format(
																new Date(log.createdAt),
																'dd/MM/yyyy HH:mm',
																{
																	locale: es,
																},
															)}
														</div>
														<span className="text-xs text-muted-foreground">
															{formatTimeAgo(log.createdAt)}
														</span>
													</div>

													<div className="mt-3 flex flex-col gap-1">
														<div className="text-xs text-muted-foreground uppercase">
															Usuario
														</div>
														<div className="text-sm font-medium">
															{log.user?.fullName ||
																log.user?.username ||
																'—'}
														</div>
														{log.user?.email && (
															<div className="text-xs text-muted-foreground">
																{log.user.email}
															</div>
														)}
													</div>

													<div className="mt-3 flex flex-col gap-1">
														<div className="text-xs text-muted-foreground uppercase">
															ID registro
														</div>
														<div className="font-mono text-sm">
															{log.idRegister}
														</div>
													</div>

													<div className="mt-3 flex flex-col gap-1">
														<div className="text-xs text-muted-foreground uppercase">
															Descripción
														</div>
														<p className="text-sm leading-relaxed">
															{log.message || 'Sin descripción'}
														</p>
													</div>
												</div>
											))}
										</div>
									</ScrollArea>

									{/* Tabla para pantallas medianas en adelante */}
									<ScrollArea className="hidden h-full w-full sm:block">
										<div className="min-w-[720px] overflow-auto pr-2">
											<Table>
												<TableHeader>
													<TableRow>
														<TableHead>Fecha</TableHead>
														<TableHead>Usuario</TableHead>
														<TableHead>ID registro</TableHead>
														<TableHead>Descripción</TableHead>
													</TableRow>
												</TableHeader>
												<TableBody>
													{logs.map(log => (
														<TableRow key={log.id}>
															<TableCell className="whitespace-nowrap">
																<div className="flex flex-col">
																	<span className="font-medium">
																		{format(
																			new Date(log.createdAt),
																			'dd/MM/yyyy HH:mm',
																			{
																				locale: es,
																			},
																		)}
																	</span>
																	<span className="text-xs text-muted-foreground">
																		{formatTimeAgo(
																			log.createdAt,
																		)}
																	</span>
																</div>
															</TableCell>
															<TableCell>
																<div className="flex flex-col">
																	<span>
																		{log.user?.fullName ||
																			log.user?.username ||
																			'—'}
																	</span>
																	{log.user?.email && (
																		<span className="text-xs text-muted-foreground">
																			{log.user.email}
																		</span>
																	)}
																</div>
															</TableCell>
															<TableCell className="font-mono text-sm">
																{log.idRegister}
															</TableCell>
															<TableCell className="max-w-[360px]">
																<p className="line-clamp-3">
																	{log.message ||
																		'Sin descripción'}
																</p>
															</TableCell>
														</TableRow>
													))}
												</TableBody>
											</Table>
										</div>
									</ScrollArea>
								</>
							)}
						</div>
					</div>

					<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
						<p className="text-sm text-muted-foreground">
							Mostrando{' '}
							<strong>
								{logs.length === 0
									? 0
									: (pagination.page - 1) * (pagination.limit ?? 10) + 1}
							</strong>{' '}
							a{' '}
							<strong>
								{(pagination.page - 1) * (pagination.limit ?? 10) + logs.length}
							</strong>{' '}
							de <strong>{totalItems}</strong> eventos
						</p>
						<div className="flex gap-2 justify-end">
							<Button
								variant="outline"
								size="sm"
								onClick={() => handlePageChange('prev')}
								disabled={pagination.page <= 1}>
								Anterior
							</Button>
							<Button
								variant="outline"
								size="sm"
								onClick={() => handlePageChange('next')}
								disabled={pagination.page >= totalPages}>
								Siguiente
							</Button>
						</div>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
};
